import { Trans } from "@lingui/macro";
import { IPeriodUnit } from "@smp/api";
import { UiBox, UiTypography } from "@smp/ui";
import { FC } from "react";

import { useContextLoader } from "@/context/loader";
import { useHumanizer } from "@/hooks/use_humanizer";
import { useLocalTimeDelta } from "@/hooks/use_local_datetime/use_local_timedelta";

import { IProductDescriptionProps } from "../../product_description";

export const CompositionPlanProduct: FC<{
  product: Extract<
    IProductDescriptionProps["product"],
    { type: "plan-product" }
  >;
}> = ({ product: { plan, period_length, period_units } }) => {
  const {
    state: { config },
  } = useContextLoader();
  const { humanizePeriodUnit } = useHumanizer();
  const formatLocalTimeDelta = useLocalTimeDelta();

  return (
    <>
      <UiBox display="flex" flexDirection="row">
        <UiBox flexGrow={1} pl={2}>
          <Trans>Using a canvas without a watermark</Trans>
        </UiBox>
        <UiBox flexGrow={1} textAlign="right">
          <Trans>Yes</Trans>
        </UiBox>
      </UiBox>
      {plan.social_pages_limit ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>billing.plan.socialPages</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.social_pages_limit}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.organization_limit && plan.organization_member_limit ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Teams/members in each</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.organization_limit}/{plan.organization_member_limit}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.autofollowers_limit ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Bouncer</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.autofollowers_limit}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.repost_configs_limit &&
      plan.repost_destination_social_pages_limit ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Reposters/Destinations in each</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.repost_configs_limit}/
            {plan.repost_destination_social_pages_limit}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.datafan_dataset_limit ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Post analytics reports</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.datafan_dataset_limit}
          </UiBox>
        </UiBox>
      ) : null}
      {config.billing.is_stream_visible &&
      plan.stream_is_allowed &&
      plan.stream_minutes_per_day ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Live Streams per day</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {formatLocalTimeDelta(
              plan.stream_minutes_per_day,
              "minutes",
              "H?'h' m?'m'",
            )}
          </UiBox>
        </UiBox>
      ) : null}
      {config.billing.is_stream_visible &&
      plan.stream_is_allowed &&
      plan.stream_resolution_max ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Live Stream quality</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.stream_resolution_max.tag}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.watermark_slots ? (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Watermarks</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            {plan.watermark_slots}
          </UiBox>
        </UiBox>
      ) : null}
      {plan.story_creator_allowed && (
        <UiBox display="flex" flexDirection="row">
          <UiBox flexGrow={1} pl={2}>
            <Trans>Instagram Story Constructor</Trans>
          </UiBox>
          <UiBox flexGrow={1} textAlign="right">
            <Trans>Yes</Trans>
          </UiBox>
        </UiBox>
      )}
      <UiBox display="flex" flexDirection="row" mb={1} mt={2}>
        <UiBox flexGrow={1}>
          <UiTypography variant="h5">
            <Trans>Duration period</Trans>
          </UiTypography>
        </UiBox>
        <UiBox flexGrow={1} textAlign="right">
          <UiTypography variant="h5">
            {period_units === IPeriodUnit.DAY && period_length === 1
              ? humanizePeriodUnit(IPeriodUnit.HOUR, period_length * 24)
              : humanizePeriodUnit(period_units, period_length)}
          </UiTypography>
        </UiBox>
      </UiBox>
    </>
  );
};
